/**
 * Firebase Analytics no cliente.
 *
 * O mesmo desenho do `app-check.ts`, pelas mesmas razões. Sem
 * `VITE_FIREBASE_MEASUREMENT_ID` nada disto se carrega, e a app funciona por
 * inteiro — o que não é só conveniência de desenvolvimento: é o que impede um
 * script da Google de ser descarregado no browser de quem corre isto
 * localmente ou publica noutro sítio qualquer.
 *
 * A importação é dinâmica de propósito. `firebase/analytics` traz o `gtag`
 * atrás; estaticamente importado, entrava no pacote principal de toda a gente,
 * incluindo de quem nunca o configurou.
 *
 * **Nada aqui pode partir a app.** Medir quem visita é a coisa menos
 * importante desta página: se o Analytics falhar — bloqueado, sem cookies,
 * offline — o arquivo tem de continuar a abrir na mesma. Daí o `catch` que
 * devolve `false` em vez de propagar.
 */

import type { FirebaseWebConfig } from './auth.js';

export interface AnalyticsDeps {
  readonly config: FirebaseWebConfig | null;
}

/** `true` se o Analytics chegou a arrancar. Devolvido para ser testável. */
export async function startAnalytics(deps: AnalyticsDeps): Promise<boolean> {
  const { config } = deps;
  if (config === null || config.measurementId === undefined) return false;

  try {
    const [app, mod] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);
    // `isSupported` e não um `try` à volta do `getAnalytics`: há browsers onde
    // o módulo carrega e falha só depois, ao escrever. Perguntar primeiro
    // evita registar um erro por cada visita num browser sem cookies.
    if (!(await mod.isSupported())) return false;

    // A mesma app do `auth` e do `app-check`. Inicializar uma segunda com a
    // mesma configuração dá um aviso do Firebase e duas instâncias a competir.
    const instance = app.getApps().length > 0 ? app.getApp() : app.initializeApp(config);
    mod.getAnalytics(instance);
    return true;
  } catch {
    return false;
  }
}
